import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const Logo = () => (
  <div className="flex items-center gap-3">
    <div className="w-9 h-9 bg-[#1A2F24] flex items-center justify-center">
      <span className="text-[#F7F6F2] font-serif text-xl leading-none">L</span>
    </div>
    <div>
      <p className="font-serif text-xl text-[#112018] leading-none">Ledgerline</p>
      <p className="label-eyebrow leading-none mt-1">CPA Operating System</p>
    </div>
  </div>
);

const AuthPage = () => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({
    name: "",
    firm_name: "",
    email: "",
    password: "",
  });
  const [busy, setBusy] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back.");
      } else {
        await register(form);
        toast.success("Firm account created.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5">
      <div
        className="hidden lg:block lg:col-span-3 relative diag-pattern"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(26,47,36,0.85), rgba(26,47,36,0.6)), url('https://images.pexels.com/photos/15757999/pexels-photo-15757999.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-[#F7F6F2]">
          <Logo />
          <div className="max-w-xl">
            <p className="label-eyebrow text-[#A8977B]">Established 2026</p>
            <h1 className="font-serif text-5xl xl:text-6xl mt-4 leading-[1.05]">
              The quiet operating system <em className="text-[#A8977B] not-italic">behind</em>{" "}
              modern accounting firms.
            </h1>
            <p className="mt-6 text-[#cfd2cc] max-w-md leading-relaxed">
              Engagement letters, document portals, AI-built mini-apps, and 500+ third-party
              integrations — woven into a single, unhurried workflow.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-8 max-w-md">
            <div>
              <p className="font-serif text-3xl text-[#A8977B]">142</p>
              <p className="label-eyebrow text-[#cfd2cc] mt-1">Firms onboarded</p>
            </div>
            <div>
              <p className="font-serif text-3xl text-[#A8977B]">4.2k</p>
              <p className="label-eyebrow text-[#cfd2cc] mt-1">Letters signed</p>
            </div>
            <div>
              <p className="font-serif text-3xl text-[#A8977B]">$31M</p>
              <p className="label-eyebrow text-[#cfd2cc] mt-1">Fees managed</p>
            </div>
          </div>
        </div>
      </div>

      <div className="col-span-1 lg:col-span-2 flex items-center justify-center p-8 bg-[#F7F6F2]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Logo />
          </div>
          <p className="label-eyebrow">
            {mode === "login" ? "Sign in" : "Create your firm"}
          </p>
          <h2 className="font-serif text-4xl text-[#112018] mt-2 leading-tight">
            {mode === "login" ? "Welcome back." : "Open the ledger."}
          </h2>
          <p className="text-[#606C64] mt-3 text-sm">
            {mode === "login"
              ? "Access your engagements, documents and team."
              : "Set up your firm in under a minute."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <>
                <div>
                  <label className="label-eyebrow block mb-2">Full name</label>
                  <input
                    data-testid="auth-name"
                    className="input-clean"
                    value={form.name}
                    onChange={onChange("name")}
                    required
                  />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Firm name</label>
                  <input
                    data-testid="auth-firm-name"
                    className="input-clean"
                    value={form.firm_name}
                    onChange={onChange("firm_name")}
                    placeholder="Ledgerline & Co."
                  />
                </div>
              </>
            )}
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <input
                data-testid="auth-email"
                type="email"
                className="input-clean"
                value={form.email}
                onChange={onChange("email")}
                required
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <input
                data-testid="auth-password"
                type="password"
                className="input-clean"
                value={form.password}
                onChange={onChange("password")}
                required
                minLength={6}
              />
            </div>

            <button
              data-testid="auth-submit"
              type="submit"
              disabled={busy}
              className="btn-primary w-full mt-4"
            >
              {busy ? "Working…" : mode === "login" ? "Enter Platform" : "Create Firm Account"}
            </button>
          </form>

          <div className="mt-6 text-sm text-[#606C64]">
            {mode === "login" ? (
              <>
                New to Ledgerline?{" "}
                <button
                  data-testid="switch-to-register"
                  onClick={() => setMode("register")}
                  className="text-[#1A2F24] underline underline-offset-4 hover:text-[#A8977B]"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already onboarded?{" "}
                <button
                  data-testid="switch-to-login"
                  onClick={() => setMode("login")}
                  className="text-[#1A2F24] underline underline-offset-4 hover:text-[#A8977B]"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
